import api from '../utils/api';

/**
 * @typedef {Object} Instructor
 * @property {string} _id
 * @property {Object} user
 * @property {string} user._id
 * @property {string} user.firstName
 * @property {string} user.lastName
 * @property {string} user.email
 * @property {string} user.username
 * @property {string} user.profilePicture
 * @property {string} professionalTitle
 * @property {string[]} expertiseAreas
 * @property {string} biography
 * @property {Object} socialMediaLinks
 * @property {string} [socialMediaLinks.linkedin]
 * @property {string} [socialMediaLinks.twitter]
 * @property {string} [socialMediaLinks.youtube]
 * @property {string} [socialMediaLinks.website]
 * @property {number} yearsOfExperience
 * @property {string} approvalStatus - 'pending', 'approved', 'rejected'
 * @property {Object} [statistics] - Only available in getInstructorById
 * @property {number} [statistics.totalCourses]
 * @property {number} [statistics.publishedCourses]
 * @property {number} [statistics.totalStudents]
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} CreateInstructorData
 * @property {string} [user] - User ID (if creating for existing user)
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} email
 * @property {string} [username]
 * @property {string} [password]
 * @property {string} [profilePicture]
 * @property {string} [phoneNumber]
 * @property {string} professionalTitle
 * @property {string[]} expertiseAreas
 * @property {string} biography
 * @property {Object} [socialMediaLinks]
 * @property {number} [yearsOfExperience]
 */

export const instructorService = {
  // ==================
  // PUBLIC ROUTES
  // ==================

  /**
   * Get all instructors with filtering and pagination
   * @param {Object} params
   * @param {number} [params.page]
   * @param {number} [params.limit]
   * @param {string} [params.search]
   * @param {string} [params.approvalStatus]
   * @param {string} [params.expertiseArea]
   * @param {string} [params.sort] - 'newest', 'experience', 'rating', 'students', 'courses'
   * @returns {Promise<{status: string, count: number, instructors: Instructor[], pagination: Object}>}
   */
  getAllInstructors: async (params = {}) => {
    const response = await api.get('/api/instructors', { params });
    return response;
  },

  /**
   * Get instructor by ID
   * @param {string} id 
   * @returns {Promise<{status: string, instructor: Instructor}>}
   */
  getInstructorById: async (id) => {
    const response = await api.get(`/api/instructors/${id}`);
    return response;
  },

  /**
   * Get instructor public courses
   * @param {string} id 
   * @param {Object} [params]
   * @returns {Promise<{status: string, count: number, courses: Object[]}>}
   */
  getInstructorCourses: async (id, params = {}) => {
    const response = await api.get(`/api/instructors/${id}/courses`, { params });
    return response;
  },

  // ==================
  // PROTECTED ROUTES (Instructor Own Profile)
  // ==================

  /**
   * Get my instructor profile
   * @returns {Promise<{status: string, instructor: Instructor}>}
   */
  getMyProfile: async () => {
    const response = await api.get('/api/instructors/me/profile');
    return response;
  },

  /**
   * Update my instructor profile
   * @param {Object} updates 
   * @returns {Promise<{status: string, instructor: Instructor}>}
   */
  updateMyProfile: async (updates) => {
    const response = await api.put('/api/instructors/me/profile', updates);
    return response;
  },

  // ==================
  // ADMIN ROUTES
  // ==================

  /**
   * Create instructor (Admin)
   * @param {CreateInstructorData} data 
   * @returns {Promise<{status: string, instructor: Instructor}>}
   */
  createInstructor: async (data) => {
    const response = await api.post('/api/instructors', data);
    return response;
  },

  /**
   * Get instructor stats (Admin)
   * @returns {Promise<{status: string, statistics: Object, topInstructors: Object}>}
   */
  getInstructorStats: async () => {
    const response = await api.get('/api/instructors/admin/stats');
    return response;
  },

  /**
   * Update instructor approval status (Admin)
   * @param {string} id 
   * @param {string} status - 'approved', 'rejected', 'pending'
   * @param {string} [rejectionReason]
   * @returns {Promise<{status: string, instructor: Instructor}>}
   */
  updateApprovalStatus: async (id, status, rejectionReason) => {
    const response = await api.patch(`/api/instructors/${id}/approval`, { 
      approvalStatus: status,
      rejectionReason 
    });
    return response;
  },

  /**
   * Update instructor details (Admin)
   * @param {string} id 
   * @param {Object} updates 
   * @returns {Promise<{status: string, instructor: Instructor}>}
   */
  updateInstructor: async (id, updates) => {
    const response = await api.put(`/api/instructors/${id}`, updates);
    return response;
  },

  /**
   * Delete instructor (Admin)
   * @param {string} id
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteInstructor: async (id) => {
    const response = await api.delete(`/api/instructors/${id}`);
    return response;
  },

  /**
   * Get total count of instructors matching the given filter
   * @param {Object} [params] - Filter params (search, approvalStatus, expertiseArea, minYearsOfExperience, fromDate, toDate)
   * @returns {Promise<{status: string, total: number}>}
   */
  getInstructorsCount: async (params = {}) => {
    const response = await api.get('/api/instructors/admin/count', { params });
    return response;
  },

  /**
   * Export all instructors matching the given filter (no pagination)
   * @param {Object} [params] - Filter params (search, approvalStatus, expertiseArea, minYearsOfExperience, fromDate, toDate)
   * @returns {Promise<{status: string, count: number, instructors: Instructor[]}>}
   */
  exportInstructors: async (params = {}) => {
    const response = await api.get('/api/instructors/admin/export', { params });
    return response;
  },
};

